import React from "react";
import { useFormContext } from "../../context/FormContext";

// Reusable Form Input Component
export const FormInput = ({
  id,
  name,
  label,
  type = "text",
  required = false,
  placeholder = "",
  min = null,
  max = null,
  style = {},
  ...props
}) => {
  const { formData, handleInputChange, errors } = useFormContext();

  return (
    <div className="form-group">
      <label htmlFor={id}>
        {label} {required && <span style={{ color: "red" }}>*</span>}
      </label>
      <input
        type={type}
        id={id}
        name={name}
        value={formData[name] || ""}
        onChange={handleInputChange}
        required={required}
        placeholder={placeholder}
        min={min}
        max={max}
        style={style}
        {...props}
      />
      {errors[name] && <div className="error-message">{errors[name]}</div>}
    </div>
  );
};

// Reusable Textarea Component
export const FormTextarea = ({
  id,
  name,
  label,
  required = false,
  placeholder = "",
  rows = 3,
  ...props
}) => {
  const { formData, handleInputChange, errors } = useFormContext();

  return (
    <div className="form-group">
      <label htmlFor={id}>
        {label} {required && <span style={{ color: "red" }}>*</span>}
      </label>
      <textarea
        id={id}
        name={name}
        value={formData[name] || ""}
        onChange={handleInputChange}
        required={required}
        placeholder={placeholder}
        rows={rows}
        {...props}
      />
      {errors[name] && <div className="error-message">{errors[name]}</div>}
    </div>
  );
};

// Reusable Select Component
export const FormSelect = ({
  id,
  name,
  label,
  options = [],
  required = false,
  ...props
}) => {
  const { formData, handleInputChange, errors } = useFormContext();

  return (
    <div className="form-group">
      <label htmlFor={id}>
        {label} {required && <span style={{ color: "red" }}>*</span>}
      </label>
      <select
        id={id}
        name={name}
        value={formData[name] || ""}
        onChange={handleInputChange}
        required={required}
        {...props}
      >
        <option value="">Select</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {errors[name] && <div className="error-message">{errors[name]}</div>}
    </div>
  );
};

// Reusable Radio Group Component
export const FormRadioGroup = ({
  name,
  label,
  options = [],
  required = false,
  ...props
}) => {
  const { formData, handleInputChange, errors } = useFormContext();

  return (
    <div className="form-group">
      <label>
        {label} {required && <span style={{ color: "red" }}>*</span>}
      </label>
      <div className="radio-group">
        {options.map((option) => (
          <label key={option.value}>
            <input
              type="radio"
              name={name}
              value={option.value}
              checked={formData[name] === option.value}
              onChange={handleInputChange}
              {...props}
            />
            {option.label}
          </label>
        ))}
      </div>
      {errors[name] && <div className="error-message">{errors[name]}</div>}
    </div>
  );
};

// Form Section Wrapper Component
export const FormSection = ({ title, children }) => {
  return (
    <div className="form-section">
      <h2 className="section-title">{title}</h2>
      {children}
    </div>
  );
};

// Form Row Component for side-by-side inputs
export const FormRow = ({ children }) => {
  return <div className="form-row">{children}</div>;
};

// Specialty Table Component
export const SpecialtyTable = () => {
  const {
    formData,
    handleSpecialtyChange,
    addSpecialtyRow,
    removeSpecialtyRow,
  } = useFormContext();

  return (
    <div>
      <table className="specialty-table">
        <thead>
          <tr>
            <th>Name of the Specialty</th>
            <th>Head of the Department</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {formData.specialties.map((specialty, index) => (
            <tr key={index}>
              <td>
                <input
                  type="text"
                  value={specialty.name}
                  onChange={(e) =>
                    handleSpecialtyChange(index, "name", e.target.value)
                  }
                  placeholder="Enter specialty name"
                />
              </td>
              <td>
                <input
                  type="text"
                  value={specialty.head}
                  onChange={(e) =>
                    handleSpecialtyChange(index, "head", e.target.value)
                  }
                  placeholder="Enter head of department"
                />
              </td>
              <td>
                {formData.specialties.length > 1 && (
                  <button
                    type="button"
                    className="remove-row-btn"
                    onClick={() => removeSpecialtyRow(index)}
                  >
                    Remove
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button type="button" className="add-row-btn" onClick={addSpecialtyRow}>
        Add Specialty
      </button>
    </div>
  );
};

// Submit Button Component
export const SubmitButton = () => {
  const {
    isSubmitting,
    submitStatus,
    validateForm,
    resetForm,
    setIsSubmitting,
    setSubmitStatus,
  } = useFormContext();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setSubmitStatus("Please fix the errors before submitting");
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus("");

    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 2000));
      console.log("Form submitted successfully!");
      setSubmitStatus("Form submitted successfully!");
      resetForm();
    } catch (error) {
      setSubmitStatus("Error submitting form. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="submit-section">
      {submitStatus && (
        <div
          className={
            submitStatus.includes("successfully")
              ? "success-message"
              : "error-message"
          }
        >
          {submitStatus}
        </div>
      )}
      <button
        type="submit"
        className="submit-btn"
        disabled={isSubmitting}
        onClick={handleSubmit}
      >
        {isSubmitting ? "Submitting..." : "Submit Application"}
      </button>
      <p style={{ marginTop: "1rem", fontSize: "0.9rem", color: "#666" }}>
        Note: Please write NA for fields that are not applicable.
      </p>
    </div>
  );
};
